import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  FasteningChoice,
  FasteningChoiceQuery,
  InsertFasteningChoiceBody,
  UpdateFasteningChoiceBody,
} from "./types/fasteningChoices";

const fasteningChoicesUrl = "/admin/settings/border/fastening_choices";

const buildFasteningChoiceUrl = (query: FasteningChoiceQuery) => {
  const params = new URLSearchParams();
  !!query.limit && params.set("limit", `${query.limit}`);
  !!query.page && params.set("page", `${query.page}`);
  !!query.q && params.set("q", query.q);
  !!query.systemId && params.set("systemId", query.systemId);
  !!query.substructureId && params.set("substructureId", query.substructureId);
  !!query.visibility && params.set("visibility", query.visibility);

  return `${fasteningChoicesUrl}${params.size > 0 ? `?${params.toString()}` : ""}`;
};

export const useFasteningChoicesApi = () => {
  const api = useApi();

  const getAdminFasteningChoice = useCallback(
    (fasteningChoiceId: string, signal: AbortSignal) => {
      const url = `${fasteningChoicesUrl}/${fasteningChoiceId}`;
      return useSWRMutation<FasteningChoice, CustomError, string>(url, (url) =>
        api.get<FasteningChoice>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminFasteningChoices = useCallback(
    (query: FasteningChoiceQuery, signal: AbortSignal) => {
      const url = buildFasteningChoiceUrl(query);
      return useSWRImmutable<{ items: FasteningChoice[]; total: number }, CustomError, string>(url, (url) =>
        api.get<{ items: FasteningChoice[]; total: number }>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminFasteningChoice = useCallback(
    async (body: InsertFasteningChoiceBody): Promise<FasteningChoice> => {
      const item = await api.post<FasteningChoice, InsertFasteningChoiceBody>(fasteningChoicesUrl, body);
      mutateCache([fasteningChoicesUrl]);
      return item;
    },
    [api],
  );

  const updateAdminFasteningChoice = useCallback(
    async (fasteningChoiceId: string, body: UpdateFasteningChoiceBody): Promise<void> => {
      const url = `${fasteningChoicesUrl}/${fasteningChoiceId}`;
      await api.put<UpdateFasteningChoiceBody>(url, body);
      mutateCache([fasteningChoicesUrl]);
    },
    [api],
  );

  const updateAdminFasteningChoiceVisibility = useCallback(
    async (fasteningChoiceId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `${fasteningChoicesUrl}/${fasteningChoiceId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([fasteningChoicesUrl]);
    },
    [api],
  );

  const updateAdminFasteningChoiceOrder = useCallback(
    async (fasteningChoiceId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `${fasteningChoicesUrl}/${fasteningChoiceId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([fasteningChoicesUrl]);
    },
    [api],
  );

  const deleteAdminFasteningChoice = useCallback(
    async (fasteningChoiceId: string): Promise<void> => {
      const url = `${fasteningChoicesUrl}/${fasteningChoiceId}`;
      await api.del(url);
      mutateCache([fasteningChoicesUrl]);
    },
    [api],
  );

  return {
    getAdminFasteningChoice,
    useAdminFasteningChoices,
    insertAdminFasteningChoice,
    updateAdminFasteningChoice,
    updateAdminFasteningChoiceVisibility,
    updateAdminFasteningChoiceOrder,
    deleteAdminFasteningChoice,
  };
};
